import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, computed, ChangeDetectorRef } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DropdownComponent } from '@shared/components/ui/dropdown/dropdown.component';
import { DropdownItemComponent } from '@shared/components/ui/dropdown/dropdown-item/dropdown-item.component';
import { NotificationService as NotificationHttpService } from '@shared/services/notification/notification.service';
import { NotificationDTO } from '@models/notification.model';

@Component({
  selector: 'app-notification-icon',
  imports: [
    CommonModule,
    RouterModule,
    DropdownComponent,
    DropdownItemComponent
  ],
  templateUrl: './notification-icon.component.html',
})
export class NotificationIconComponent implements OnInit, OnDestroy {
  isOpen = false;

  // Computed properties
  notifying = computed(() => this.notificationService.unreadCount() > 0);
  notifications = computed(() => this.notificationService.recentNotifications());
  unreadCount = computed(() => this.notificationService.unreadCount());

  constructor(
    private notificationService: NotificationHttpService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Cargar conteo inicial de notificaciones no leídas
    this.notificationService.refreshUnreadCount();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    
    // Cargar notificaciones recientes solo cuando se abre el dropdown
    if (this.isOpen) {
      this.notificationService.refreshRecentNotifications();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  ngOnDestroy(): void {
    // Placeholder — sin recursos que limpiar actualmente
  }

  markAsRead(notification: NotificationDTO): void {
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.id).subscribe({
        next: () => {
          this.notificationService.refreshRecentNotifications();
        },
        error: (err) => console.error('Error al marcar notificación como leída', err)
      });
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notificationService.refreshRecentNotifications();
      },
      error: (err) => console.error('Error al marcar todas como leídas', err)
    });
  }

  getTimeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hr ago`;
    return `${days} días ago`;
  }

  getNotificationIcon(type: string): string {
    // Retornar diferentes iconos según el tipo
    switch (type) {
      case 'change_order_created':
      case 'change_order_approved':
      case 'change_order_rejected':
        return 'images/icons/change-order.png';
      case 'project_assigned':
        return 'images/icons/project.png';
      default:
        return 'images/user/user-02.jpg';
    }
  }

  formatNotificationMessage(message: string): string {
    // Buscar patrones de texto que deberían ir en negrita
    // Patrón: "nombre ha solicitado cambios en proyecto"
    const pattern = /^(.+?)\s+ha\s+solicitado\s+cambios\s+en\s+(.+)$/i;
    const match = message.match(pattern);
    
    if (match) {
      const userName = match[1];
      const projectName = match[2];
      return `<strong>${userName}</strong> ha solicitado cambios en <strong>${projectName}</strong>`;
    }
    
    // Si no coincide el patrón, retornar el mensaje original
    return message;
  }

  /**
   * Extrae las iniciales de las primeras 2 palabras del mensaje
   * Ejemplo: "Partner Tech ha solicitado cambios" -> "PT"
   */
  getInitialsFromMessage(message: string): string {
    if (!message) return '??';
    
    const words = message.trim().split(' ');
    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    
    // Tomar las primeras 2 palabras
    const firstWord = words[0];
    const secondWord = words[1];
    
    const initial1 = firstWord.charAt(0).toUpperCase();
    const initial2 = secondWord.charAt(0).toUpperCase();
    
    return initial1 + initial2;
  }

  /**
   * Genera un color consistente basado en el userId
   * Mismo userId siempre tendrá el mismo color
   */
  getAvatarColor(userId: number): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-cyan-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-yellow-500'
    ];
    
    return colors[userId % colors.length];
  }
}